import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Search, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { SectionNode, WebsiteDefinition } from "@baze/schema";
import type { PublishIssue } from "@baze/api-client";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/select";
import { t } from "@/i18n/id";
import { cn } from "@/lib/cn";
import { ListFocusContext, SectionForm } from "../content-form";
import { updateContent } from "../model/ops";
import { getPath } from "../model/paths";
import { LIST_SPECS, type ListSpec } from "./specs";

type Apply = (next: WebsiteDefinition, key?: string) => void;
type Item = Record<string, unknown>;

/**
 * The large workspace for a section with lists (lessons, menu, pricing…): a tree of its items on the
 * left, the selected item's full form on the right (or the section's own settings). The forms are the
 * inspector's own (SectionForm), narrowed to one item through ListFocusContext. On phones the tree and
 * the form are two steps.
 */
export function ListWorkspace({
  siteId,
  def,
  section,
  apply,
  selected,
  onSelect,
  stacked,
  issues,
}: {
  siteId: string;
  def: WebsiteDefinition;
  section: SectionNode;
  apply: Apply;
  /** Content path of the item being edited; null for the section settings. */
  selected: string[] | null;
  onSelect: (path: string[] | null) => void;
  /** Too narrow for tree and form side by side (phones, a narrow panel): show them as two steps. */
  stacked: boolean;
  issues?: PublishIssue[];
}) {
  const spec = LIST_SPECS[section.type]!;
  const content = section.content as Item;
  const [query, setQuery] = useState("");
  const [closed, setClosed] = useState<ReadonlySet<number>>(new Set());
  const [showForm, setShowForm] = useState(selected !== null);
  const selectedKey = selected?.join(".") ?? "";
  const [seenKey, setSeenKey] = useState(selectedKey);
  if (selectedKey !== seenKey) {
    setSeenKey(selectedKey);
    if (selected) setShowForm(true);
  }
  // Undo toasts run later: work on the section as it is then.
  const latest = useRef({ def, section });
  useEffect(() => {
    latest.current = { def, section };
  }, [def, section]);

  const listAt = (path: string[], from: Item = content) => ((getPath(from, path.join(".")) as Item[] | undefined) ?? []);
  const write = (path: string[], value: Item[], base = def) => apply(updateContent(base, section.id, path.join("."), value), `${section.id}:${path.join(".")}`);
  const select = (path: string[] | null) => {
    onSelect(path);
    setShowForm(true);
  };

  const add = (listPath: string[], s: ListSpec) => {
    const list = listAt(listPath);
    if (list.length >= s.max) return;
    write(listPath, [...list, s.blank(list.length)]);
    select([...listPath, String(list.length)]);
  };
  const move = (listPath: string[], from: number, to: number) => {
    const list = listAt(listPath);
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row!);
    write(listPath, next);
    const key = [...listPath, String(from)].join(".");
    if (selectedKey === key || selectedKey.startsWith(`${key}.`)) onSelect([...listPath, String(to), ...selected!.slice(listPath.length + 1)]);
  };
  const remove = (listPath: string[], index: number, s: ListSpec) => {
    const removed = listAt(listPath)[index]!;
    write(
      listPath,
      listAt(listPath).filter((_, i) => i !== index),
    );
    onSelect(listPath.length > 1 ? listPath.slice(0, -1) : null);
    toast(t.editor.inspector.itemRemoved(s.titleOf(removed) || t.editor.inspector.itemN(s.label, index + 1)), {
      action: {
        label: t.common.undo,
        onClick: () => {
          const now = listAt(listPath, latest.current.section.content as Item);
          write(listPath, [...now.slice(0, index), removed, ...now.slice(index)], latest.current.def);
          onSelect([...listPath, String(index)]);
        },
      },
    });
  };
  /** Move a nested item (a topic) into another group, at its end. */
  const moveTo = (from: string[], target: number) => {
    const child = spec.child!;
    const groups = listAt([spec.path]);
    const gi = Number(from[1]);
    const ti = Number(from[3]);
    if (target === gi) return;
    const row = (groups[gi]![child.path] as Item[])[ti]!;
    const next = groups.map((g, i) => {
      const rows = g[child.path] as Item[];
      if (i === gi) return { ...g, [child.path]: rows.filter((_, j) => j !== ti) };
      if (i === target) return { ...g, [child.path]: [...rows, row] };
      return g;
    });
    write([spec.path], next);
    onSelect([spec.path, String(target), child.path, String((groups[target]![child.path] as Item[]).length)]);
  };

  const q = query.trim().toLowerCase();
  const hit = (s: string) => !q || s.toLowerCase().includes(q);
  const top = listAt([spec.path]);
  const rows = top.flatMap((item, i) => {
    const children = spec.child ? ((item[spec.child.path] as Item[] | undefined) ?? []) : [];
    const own = hit(spec.titleOf(item));
    const kids = children.map((c, j) => ({ c, j })).filter(({ c }) => own || hit(spec.child!.titleOf(c)));
    return own || kids.length ? [{ item, i, kids }] : [];
  });
  const inChild = !!spec.child && selected?.length === 4;

  const tree = (
    <nav aria-label={spec.label} className={cn("flex min-h-0 flex-col", !stacked && "w-[260px] shrink-0 border-r border-line")}>
      <div className="p-3">
        <Input inputSize="sm" type="search" value={query} placeholder={t.editor.workspace.search} aria-label={t.editor.workspace.search} leading={<Search className="size-4" aria-hidden="true" />} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <div className="scrollbar-thin min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-4">
        <TreeRow active={selected === null} onClick={() => select(null)} title={t.editor.workspace.settings} icon={<Settings2 className="size-4" aria-hidden="true" />} />
        <p className="px-2 pt-3 pb-1 text-xs font-bold tracking-wide text-slate-500 uppercase">{spec.label}</p>
        {rows.map(({ item, i, kids }) => {
          const path = [spec.path, String(i)];
          const open = !!q || !closed.has(i) || (selected?.[1] === String(i) && inChild);
          const child = spec.child;
          return (
            <div key={i}>
              <TreeRow
                active={selectedKey === path.join(".")}
                number={i + 1}
                title={spec.titleOf(item)}
                expanded={child ? open : undefined}
                onToggle={() => setClosed((cur) => { const next = new Set(cur); if (next.has(i)) next.delete(i); else next.add(i); return next; })}
                onClick={() => select(path)}
                onUp={i > 0 ? () => move([spec.path], i, i - 1) : undefined}
                onDown={i < top.length - 1 ? () => move([spec.path], i, i + 1) : undefined}
                onRemove={top.length > 1 ? () => remove([spec.path], i, spec) : undefined}
              />
              {child && open ? (
                <div className="ml-4 space-y-0.5 border-l border-line pl-2">
                  {kids.map(({ c, j }) => {
                    const childPath = [...path, child.path, String(j)];
                    const siblings = (item[child.path] as Item[]).length;
                    return (
                      <TreeRow
                        key={j}
                        active={selectedKey === childPath.join(".")}
                        number={j + 1}
                        title={child.titleOf(c)}
                        onClick={() => select(childPath)}
                        onUp={j > 0 ? () => move([...path, child.path], j, j - 1) : undefined}
                        onDown={j < siblings - 1 ? () => move([...path, child.path], j, j + 1) : undefined}
                        onRemove={siblings > 1 ? () => remove([...path, child.path], j, child) : undefined}
                      />
                    );
                  })}
                  {q ? null : <AddRow label={child.label} full={(item[child.path] as Item[]).length >= child.max} onClick={() => add([...path, child.path], child)} />}
                </div>
              ) : null}
            </div>
          );
        })}
        {q && !rows.length ? <p className="px-2 py-3 text-sm text-slate-500">{t.editor.workspace.empty}</p> : null}
        {q ? null : <AddRow label={spec.label} full={top.length >= spec.max} onClick={() => add([spec.path], spec)} />}
      </div>
    </nav>
  );

  const title = selected === null ? t.editor.workspace.settings : nodeTitle(spec, content, selected);
  const form = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5 lg:px-6">
        {stacked ? (
          <Button variant="ghost" size="icon-sm" onClick={() => setShowForm(false)} aria-label={t.editor.workspace.back}>
            <ArrowLeft aria-hidden="true" />
          </Button>
        ) : null}
        <p className="min-w-0 flex-1 truncate font-semibold text-slate-900">{title}</p>
        {inChild && top.length > 1 ? (
          <label className="flex items-center gap-2 text-xs font-semibold whitespace-nowrap text-slate-600">
            {t.editor.workspace.moveTo}
            <NativeSelect selectSize="sm" value={selected![1]} onChange={(e) => moveTo(selected!, Number(e.target.value))}>
              {top.map((g, i) => (
                <option key={i} value={i}>
                  {spec.titleOf(g) || t.editor.inspector.itemN(spec.label, i + 1)}
                </option>
              ))}
            </NativeSelect>
          </label>
        ) : null}
      </div>
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-4 py-5 lg:px-6">
        {selected === null && issues?.length ? (
          <div className="mb-4 space-y-2">
            {issues.map((issue, i) => (
              <Alert key={`${issue.code}-${i}`} tone="warning">
                {issue.message}
              </Alert>
            ))}
          </div>
        ) : null}
        <ListFocusContext.Provider value={{ focus: selected ? { mode: "item", path: selected } : { mode: "settings" }, roomy: true }}>
          {/* An item: only its own list survives; the section's heading fields stay hidden. */}
          <div key={selectedKey || "settings"} className={cn("mx-auto max-w-2xl", selected && "[&>div>:not([data-item-stack])]:hidden")}>
            <SectionForm siteId={siteId} def={def} section={section} apply={apply} />
          </div>
        </ListFocusContext.Provider>
      </div>
    </div>
  );

  return <div className="flex h-full min-h-0">{!stacked ? <>{tree}{form}</> : showForm ? form : <div className="flex min-h-0 flex-1 flex-col">{tree}</div>}</div>;
}

function nodeTitle(spec: ListSpec, content: Item, path: string[]): string {
  const item = getPath(content, path.join(".")) as Item | undefined;
  const s = path.length === 4 && spec.child ? spec.child : spec;
  return (item && s.titleOf(item)) || t.editor.workspace.untitled;
}

function TreeRow({
  active,
  title,
  number,
  icon,
  expanded,
  onToggle,
  onClick,
  onUp,
  onDown,
  onRemove,
}: {
  active: boolean;
  title: string;
  number?: number;
  icon?: React.ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  onClick: () => void;
  onUp?: () => void;
  onDown?: () => void;
  onRemove?: () => void;
}) {
  const label = title || t.editor.workspace.untitled;
  const action = "grid size-7 place-items-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 disabled:opacity-30";
  return (
    <div className={cn("group flex items-center gap-0.5 rounded-lg pr-1", active ? "bg-primary-soft" : "hover:bg-slate-50")}>
      {expanded !== undefined ? (
        <button type="button" aria-expanded={expanded} aria-label={label} onClick={onToggle} className="grid size-7 shrink-0 place-items-center rounded-md text-slate-400 hover:text-slate-900">
          <ChevronDown className={cn("size-4 transition-transform", !expanded && "-rotate-90")} aria-hidden="true" />
        </button>
      ) : null}
      <button type="button" onClick={onClick} aria-current={active ? "true" : undefined} className={cn("flex min-h-9 min-w-0 flex-1 items-center gap-2 px-2 text-left text-sm", active ? "font-semibold text-primary" : "text-slate-800")}>
        {icon ?? (number !== undefined ? <span className="w-5 shrink-0 text-right text-xs text-slate-400 tabular-nums">{number}</span> : null)}
        <span className={cn("min-w-0 flex-1 truncate", !title && "text-slate-400 italic")}>{label}</span>
      </button>
      {onUp || onDown || onRemove ? (
        // Only on the selected row, or on hover and keyboard focus: titles keep the room otherwise.
        <span className={cn("shrink-0 items-center", active ? "flex" : "hidden group-focus-within:flex group-hover:flex")}>
          <button type="button" className={action} disabled={!onUp} onClick={onUp} aria-label={`${t.editor.inspector.moveItemUp}: ${label}`}>
            <ChevronUp className="size-3.5" aria-hidden="true" />
          </button>
          <button type="button" className={action} disabled={!onDown} onClick={onDown} aria-label={`${t.editor.inspector.moveItemDown}: ${label}`}>
            <ChevronDown className="size-3.5" aria-hidden="true" />
          </button>
          <button type="button" className={cn(action, "hover:text-danger")} disabled={!onRemove} onClick={onRemove} aria-label={`${t.editor.inspector.removeItem}: ${label}`}>
            <Trash2 className="size-3.5" aria-hidden="true" />
          </button>
        </span>
      ) : null}
    </div>
  );
}

function AddRow({ label, full, onClick }: { label: string; full: boolean; onClick: () => void }) {
  return (
    <button type="button" disabled={full} onClick={onClick} title={full ? t.editor.workspace.full : undefined} className="flex min-h-9 w-full items-center gap-2 rounded-lg px-2 text-sm font-semibold text-primary hover:bg-primary-soft/60 disabled:text-slate-400 disabled:hover:bg-transparent">
      <Plus className="size-4" aria-hidden="true" />
      {t.editor.inspector.addItem} · {label.toLowerCase()}
    </button>
  );
}
